import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Pagination,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Tooltip
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { breadcrumbAction } from "../../redux/actions/config.action";
import {
  uploadAdminCdnImage,
  getAdminCdnImages,
  uploadMerchantCdnImage,
  getMerchantCdnImages,
  deleteAdminCdnImage,
  deleteMerchantCdnImage
} from "../../services/cdnUpload.service";
import { showToast, startLoader, stopLoader } from "../../lib/global";
import moment from "moment";

const CdnImageUpload = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [role, setRole] = useState(localStorage.getItem("role") || "");
  const isMerchant = role === "MERCHANT";

  const [imagesList, setImagesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [isUploading, setIsUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Set breadcrumbs & Load initial data
  useEffect(() => {
    const label = isMerchant ? "Merchant CDN Upload" : "Admin CDN Upload";
    const breadcrumb = [{ label, url: null }];
    dispatch(breadcrumbAction(breadcrumb));
    fetchImages();
  }, [dispatch, page, searchQuery, role]);

  const fetchImages = () => {
    startLoader();
    const query = {
      search: searchQuery,
      limit,
      page
    };

    const fetchAction = isMerchant ? getMerchantCdnImages(query) : getAdminCdnImages(query);

    dispatch(fetchAction)
      .then((res) => {
        stopLoader();
        if (res && res.data && res.data.status === 200) {
          setImagesList(res.data.data || []);
          if (res.data.pagination) {
            setTotalItems(res.data.pagination.totalItems || 0);
            setTotalPages(res.data.pagination.totalPages || 1);
          }
        } else {
          showToast(res?.data?.message || "Failed to fetch uploads", "error");
        }
      })
      .catch((err) => {
        stopLoader();
        console.error("Fetch images error:", err);
        showToast("Error fetching images list", "error");
      });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset page on new search
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    startLoader();

    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        showToast(`${file.name} is not a valid image file.`, "error");
        failCount++;
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);

      const uploadAction = isMerchant ? uploadMerchantCdnImage(formData) : uploadAdminCdnImage(formData);

      try {
        const res = await dispatch(uploadAction);
        if (res && res.data && res.data.status === 200) {
          successCount++;
        } else {
          failCount++;
          showToast(`Failed to upload ${file.name}: ${res?.data?.message || "Server Error"}`, "error");
        }
      } catch (err) {
        failCount++;
        console.error("Upload error:", err);
      }
    }

    setIsUploading(false);
    stopLoader();

    if (successCount > 0) {
      showToast(`Successfully uploaded ${successCount} image(s).`, "success");
      fetchImages();
    }
    if (failCount > 0) {
      showToast(`Failed to upload ${failCount} file(s).`, "error");
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCopyLink = (cdnUrl, recordId) => {
    navigator.clipboard
      .writeText(cdnUrl)
      .then(() => {
        setCopiedId(recordId);
        showToast("CDN URL copied to clipboard!", "success");
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch((err) => {
        console.error("Copy failed:", err);
        showToast("Failed to copy link", "error");
      });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this image? It will be removed from DigitalOcean Spaces and our database.")) {
      startLoader();
      const deleteAction = isMerchant ? deleteMerchantCdnImage(id) : deleteAdminCdnImage(id);
      dispatch(deleteAction)
        .then((res) => {
          stopLoader();
          if (res && res.data && res.data.status === 200) {
            showToast("Image deleted successfully!", "success");
            fetchImages();
          } else {
            showToast(res?.data?.message || "Failed to delete image", "error");
          }
        })
        .catch((err) => {
          stopLoader();
          console.error("Delete image error:", err);
          showToast("Error deleting image", "error");
        });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isUploading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const eventMock = { target: { files } };
      handleFileChange(eventMock);
    }
  };

  return (
    <div className="container-fluid py-4">
      <Card sx={{ borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", mb: 4 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "#1a202c" }}>
            CDN Image Uploader
          </Typography>
          <Typography variant="body2" sx={{ color: "#718096", mb: 3 }}>
            Upload your images here. They will be automatically compressed and optimized. Copy the CDN links to your Excel sheet imports.
          </Typography>

          {/* Drag & Drop Upload Zone */}
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            style={{
              border: "2px dashed #cbd5e0",
              borderRadius: "8px",
              padding: "40px 20px",
              textAlign: "center",
              cursor: isUploading ? "not-allowed" : "pointer",
              backgroundColor: "#f7fafc",
              transition: "border-color 0.2s, background-color 0.2s"
            }}
            className="upload-dropzone"
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              accept="image/*"
              style={{ display: "none" }}
              disabled={isUploading}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: "#4a5568", mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#2d3748", fontSize: "16px", fontWeight: 500 }}>
              {isUploading ? "Uploading & Compressing..." : "Drag and drop your images here, or click to browse"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#a0aec0", display: "block", mt: 1 }}>
              Supports JPG, JPEG, PNG, WEBP (Max 10MB per image)
            </Typography>
          </div>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#2d3748" }}>
              Upload Audit History ({totalItems} files)
            </Typography>
            <TextField
              placeholder="Search by filename..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "#a0aec0", mr: 1, fontSize: 20 }} />
              }}
              sx={{ width: { xs: "100%", sm: "300px" } }}
            />
          </Stack>

          <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ backgroundColor: "#f7fafc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#4a5568" }}>Thumbnail</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#4a5568" }}>Filename</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#4a5568" }}>CDN Link</TableCell>
                  {!isMerchant && <TableCell sx={{ fontWeight: 600, color: "#4a5568" }}>Uploaded By</TableCell>}
                  <TableCell sx={{ fontWeight: 600, color: "#4a5568" }}>Uploaded At</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: "#4a5568" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {imagesList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMerchant ? 5 : 6} align="center" sx={{ py: 6, color: "#a0aec0" }}>
                      No uploaded images found.
                    </TableCell>
                  </TableRow>
                ) : (
                  imagesList.map((record) => (
                    <TableRow key={record.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell>
                        <a href={record.cdnUrl} target="_blank" rel="noopener noreferrer">
                          <img
                            src={record.thumbnailUrl}
                            alt={record.filename}
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              border: "1px solid #e2e8f0",
                              cursor: "zoom-in"
                            }}
                          />
                        </a>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#2d3748" }}>{record.filename}</TableCell>
                      <TableCell sx={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Tooltip title={record.cdnUrl}>
                          <span style={{ fontSize: "13px", color: "#4a5568" }}>{record.cdnUrl}</span>
                        </Tooltip>
                      </TableCell>
                      {!isMerchant && (
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#2d3748" }}>
                            {record.uploadedBy}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#718096" }}>
                            {record.uploaderRole}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ color: "#4a5568" }}>
                        {moment(record.createdAt).format("DD MMM YYYY, HH:mm")}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Copy CDN URL">
                            <IconButton
                              onClick={() => handleCopyLink(record.cdnUrl, record.id)}
                              color={copiedId === record.id ? "success" : "default"}
                              sx={{
                                border: "1px solid",
                                borderColor: copiedId === record.id ? "#48bb78" : "#e2e8f0",
                                padding: "6px"
                              }}
                            >
                              {copiedId === record.id ? (
                                <CheckIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open Image">
                            <IconButton
                              component="a"
                              href={record.cdnUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                border: "1px solid #e2e8f0",
                                padding: "6px"
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 16, color: "#4a5568" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Image">
                            <IconButton
                              onClick={() => handleDelete(record.id)}
                              color="error"
                              sx={{
                                border: "1px solid #fed7d7",
                                padding: "6px"
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Stack spacing={2} sx={{ mt: 3, itemsAlign: "center" }} alignItems="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
              />
            </Stack>
          )}
        </CardContent>
      </Card>
      
      <style jsx="true">{`
        .upload-dropzone:hover {
          border-color: #4299e1 !important;
          background-color: #ebf8ff !important;
        }
      `}</style>
    </div>
  );
};

export default CdnImageUpload;
